/**
 * Funções puras da aba de histórico do médico. Ficam fora do componente para que a
 * classificação das movimentações possa ser testada sem TestBed.
 */

/** Famílias de movimentação; cada uma tem uma cor de badge na timeline. */
export type FamiliaMovimentacao = 'inscricao' | 'transferencia' | 'secundaria' | 'reinscricao' | 'encerramento' | 'outro';

/** Uma linha do histórico já normalizada: a timeline não precisa saber de qual aba ela veio. */
export interface MovimentacaoHistorico {
    /** Posição na lista de origem — é por ela que a seleção devolve a linha exata. */
    indiceLinha: number;
    idModoInscricao: number;
    descricao: string;
    /** UF de origem/destino da movimentação; pode vir vazia. */
    ufMovimentacao: string;
    /** UF do próprio registro; nunca vem vazia, serve de reserva para a bandeira. */
    ufRegistro: string;
    /** dd/mm/aaaa */
    data: string;
    dataCancelamento?: string;
    motivoCancelamento?: string;
    /** Rótulo da UF na aba de origem: 'Origem/Destino' ou 'UF Destino'. */
    rotuloUf: string;
}

/** Um item desenhado pela timeline. */
export interface EventoInscricao {
    indiceLinha: number;
    data: string;
    ordenacao: number;
    uf: string;
    bandeira: string;
    descricao: string;
    familia: FamiliaMovimentacao;
    cancelada: boolean;
    dataCancelamento?: string;
    motivoCancelamento?: string;
    rotuloUf: string;
}

/**
 * Classificação por ID, e não pelo texto da descrição: as descrições da tabela MODO_INSCRICAO
 * têm acentuação inconsistente ('INSCRICAO SECUNDARIA' convive com 'INSCRIÇÃO SECUNDÁRIA COM
 * TRANSFORMAÇÃO') e casar por substring erraria em casos como 'REINSCRICAO POR TRANSFERENCIA'.
 * IDs ausentes daqui caem em 'outro' — inclusive os que o banco vier a criar.
 */
const FAMILIA_POR_MODO_INSCRICAO: ReadonlyMap<number, FamiliaMovimentacao> = new Map<number, FamiliaMovimentacao>([
    [1, 'inscricao'], // PRIMEIRA INSCRICAO
    [14, 'inscricao'], // INSCRICAO PROVISORIA
    [16, 'inscricao'], // INSCRIÇÃO TEMPORÁRIA - ESTRANGEIRO
    [19, 'inscricao'], // PMM - INSCRIÇÃO

    [2, 'transferencia'], // INSCRICAO POR TRANSFERENCIA
    [4, 'transferencia'], // REINSCRICAO POR TRANSFERENCIA
    [7, 'transferencia'], // TRANSFERENCIA PARA OUTRA UF
    [8, 'transferencia'], // INSCRIÇÃO POR TRANSFERÊNCIA COM TRANSFORMAÇÃO
    [17, 'transferencia'], // INSCRICAO PROVISORIA POR TRANSFERENCIA
    [21, 'transferencia'], // PMM - TRANSFERÊNCIA PARA OUTRA UF
    [22, 'transferencia'], // PMM - REINSCRIÇÃO POR TRANSFERÊNCIA

    [3, 'secundaria'], // INSCRICAO SECUNDARIA
    [5, 'secundaria'], // REINSCRICAO SECUNDARIA
    [6, 'secundaria'], // INSCRICAO SECUNDARIA - OUTRA UF
    [11, 'secundaria'], // INSCRIÇÃO SECUNDÁRIA COM TRANSFORMAÇÃO
    [24, 'secundaria'], // REINSCRIÇÃO COM TRANSFORMAÇÃO DE PMM EM SECUNDÁRIA
    [25, 'secundaria'], // INSCRICAO PROVISORIA SECUNDARIA
    [27, 'secundaria'], // INSCRICAO SECUNDARIA PROVISORIA - OUTRA UF

    [9, 'reinscricao'], // REINSCRIÇÃO PRINCIPAL
    [10, 'reinscricao'], // REINSCRICAO DEVOLUCAO RES. 1299/89
    [12, 'reinscricao'], // REINSCRIÇÃO COM TRANSFORMAÇÃO DE PMM EM PRINCIPAL
    [15, 'reinscricao'], // REINSCRICAO PROVISORIA
    [20, 'reinscricao'], // PMM - REINSCRIÇÃO
    [23, 'reinscricao'], // REATIVAÇÃO
    [26, 'reinscricao'], // REGULARIZAÇÃO

    [18, 'encerramento'] // FALECIMENTO
]);

const UF_PADRAO = 'BR';

export function familiaDoModoInscricao(id: number | undefined | null): FamiliaMovimentacao {
    if (id === undefined || id === null) return 'outro';
    return FAMILIA_POR_MODO_INSCRICAO.get(id) ?? 'outro';
}

export function bandeiraDaUf(sigla: string | undefined | null): string {
    const normalizada = String(sigla ?? '')
        .trim()
        .toUpperCase();
    return `images/estados/${normalizada || UF_PADRAO}.png`;
}

/**
 * Converte para timestamp comparável tanto o ISO (`aaaa-mm-dd`, como a API devolve o histórico
 * cru) quanto o dd/mm/aaaa (como a tela já formata antes de montar os eventos da timeline).
 * Qualquer outra coisa tenta o `Date.parse` do runtime; se nem isso resolver, vai para o fim da
 * lista em vez de virar NaN, que embaralharia a ordenação inteira. Único parser de data da aba de
 * histórico — usado tanto para ordenar a carga (`Historico.carregarSituacao`/`carregarSecundarias`)
 * quanto para ordenar os eventos da timeline (`paraEventosTimeline`), para as duas visões nunca
 * discordarem da ordem dos mesmos dados.
 */
export function dataParaTimestamp(data: string | undefined | null): number {
    if (!data) return Number.NEGATIVE_INFINITY;

    const iso = String(data).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const [, yyyy, mm, dd] = iso;
        return Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd));
    }

    const br = String(data).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) {
        const [, dd, mm, yyyy] = br;
        return Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd));
    }

    const parsed = Date.parse(String(data));
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

export function paraEventosTimeline(movimentacoes: readonly MovimentacaoHistorico[]): EventoInscricao[] {
    return movimentacoes
        .map((movimentacao) => {
            const uf = String(movimentacao.ufMovimentacao || movimentacao.ufRegistro || '')
                .trim()
                .toUpperCase();
            return {
                indiceLinha: movimentacao.indiceLinha,
                data: movimentacao.data,
                ordenacao: dataParaTimestamp(movimentacao.data),
                uf,
                bandeira: bandeiraDaUf(uf),
                descricao: movimentacao.descricao,
                familia: familiaDoModoInscricao(movimentacao.idModoInscricao),
                cancelada: !!movimentacao.dataCancelamento,
                dataCancelamento: movimentacao.dataCancelamento || undefined,
                motivoCancelamento: movimentacao.motivoCancelamento || undefined,
                rotuloUf: movimentacao.rotuloUf
            };
        })
        .sort((a, b) => b.ordenacao - a.ordenacao);
}
